import { translate as t } from '@thoughtspot/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAdminContext } from "../../../contexts/context";
import { useFetchPinboardDetail, useGetPinboardListQuery, } from "../../../services/pinboard-list/pinboard-list-service";
import { updateConfigInfo } from "../../../services/system/system-service";
import { create } from '../../../utils/logger';
import { downloadLiveboardTranslations, } from './liveboard-translation-download.util';
import { ManualTranslationToggle } from './manual-translation-toggle';
import { useTranslationUpload } from './translation-upload.hook';
const logger = create('Admin.ManualTranslation');
const transformToLiveboardData = (pinboard, fallbackName) => {
    return {
        name: pinboard?.name || fallbackName,
        description: pinboard?.description || null,
        containers: pinboard?.containers?.map(container => ({
            name: container?.name || null,
            description: container?.description || null,
            type: container?.type || null,
            noteTile: container?.noteTile
                ? {
                    htmlParsedString: container.noteTile.htmlParsedString || null,
                }
                : null,
        })) || [],
        tabs: pinboard?.tabs?.map(tab => ({
            name: tab?.name || null,
        })) || [],
    };
};
export const ManualTranslationToggleContainer = ({ orgViewModeType, onEditingChange, onLoadingChange, onSavingChange, onShowToast, }) => {
    const { sessionService } = useAdminContext();
    const isOrgEnabled = sessionService.isOrgUiEnabled();
    const isAllOrgContext = !isOrgEnabled || orgViewModeType === 'all-org';
    const isMountedRef = useRef(true);
    const [enabled, setEnabled] = useState(false);
    const [clusterEnabled, setClusterEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [pendingValue, setPendingValue] = useState(false);
    const [selectedLiveboard, setSelectedLiveboard] = useState(null);
    const [selectorSearchPattern, setSelectorSearchPattern] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const { uploadCSV, uploading, uploadError, clearUploadError, setUploadError, checkCsvExists, downloadExistingCsv, downloadingExistingCsv, existingCsvDownloadError, clearExistingCsvDownloadError, } = useTranslationUpload();
    const [csvExists, setCsvExists] = useState(null);
    const handleUploadCSV = useCallback(async (file) => {
        if (!enabled) {
            const error = t('adminSection.manualTranslation.uploadNotEnabled');
            setUploadError(error);
            return Promise.resolve({ success: false, error });
        }
        const result = await uploadCSV(file);
        if (result.success) {
            setCsvExists(true);
        }
        return result;
    }, [enabled, uploadCSV, setUploadError]);
    const canModify = isAllOrgContext || clusterEnabled;
    const perOrgMapRef = useRef({});
    const releaseVersion = sessionService.getBaseReleaseVersion();
    const LEARN_MORE_URL = `https://docs.thoughtspot.com/cloud/${releaseVersion}/manual-translation`;
    const { data: pinboardListData, loading: pinboardListLoading, } = useGetPinboardListQuery({ batchSize: 1000, offset: 0 }, !isEditing || !pendingValue);
    const { fetchPinboardDetail } = useFetchPinboardDetail();
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const fetchConfig = () => {
        const configInfo = sessionService.getSessionInfo()?.configInfo;
        const clusterValue = Boolean(configInfo?.manualTranslationOfObjectMetadata);
        const sessionPerOrg = configInfo?.manualTranslationOfObjectMetadataPerOrg || {};
        perOrgMapRef.current = isAllOrgContext
            ? sessionPerOrg
            : { ...perOrgMapRef.current, ...sessionPerOrg };
        setClusterEnabled(clusterValue);
        if (isAllOrgContext) {
            setEnabled(clusterValue);
            setPendingValue(clusterValue);
        }
        else if (!clusterValue) {
            setEnabled(false);
            setPendingValue(false);
        }
        else {
            const orgId = sessionService.getCurrentOrgId().toString();
            const overrideValue = perOrgMapRef.current[orgId] !== undefined
                ? Boolean(perOrgMapRef.current[orgId])
                : undefined;
            const effectiveValue = overrideValue !== undefined ? overrideValue : clusterValue;
            setEnabled(effectiveValue);
            setPendingValue(effectiveValue);
        }
        setLoading(false);
    };
    useEffect(() => {
        fetchConfig();
    }, [orgViewModeType]);
    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);
    useEffect(() => {
        onSavingChange?.(saving);
    }, [saving, onSavingChange]);
    const handleSave = async (newValue) => {
        try {
            setSaving(true);
            let apiChanges;
            let sessionUpdate;
            if (isAllOrgContext) {
                apiChanges = {
                    manualTranslationOfObjectMetadata: newValue,
                    ...(newValue && {
                        manualTranslationOfObjectMetadataPerOrg: {},
                    }),
                };
                sessionUpdate = { ...apiChanges };
                if (newValue)
                    perOrgMapRef.current = {};
            }
            else {
                const orgId = sessionService.getCurrentOrgId().toString();
                perOrgMapRef.current = {
                    ...perOrgMapRef.current,
                    [orgId]: newValue,
                };
                apiChanges = {
                    manualTranslationOfObjectMetadataPerOrg: {
                        [orgId]: newValue,
                    },
                };
                sessionUpdate = {
                    manualTranslationOfObjectMetadataPerOrg: {
                        ...perOrgMapRef.current,
                    },
                };
            }
            await updateConfigInfo(apiChanges);
            if (!isMountedRef.current)
                return;
            setEnabled(newValue);
            setPendingValue(newValue);
            if (isAllOrgContext)
                setClusterEnabled(newValue);
            const sessionInfo = sessionService.getSessionInfo();
            if (sessionInfo) {
                sessionService.setSessionInfo({
                    ...sessionInfo,
                    configInfo: {
                        ...sessionInfo.configInfo,
                        ...sessionUpdate,
                    },
                });
            }
            setIsEditing(false);
            onEditingChange?.(false);
            onShowToast?.(t('tscli.ui.appsettings.datasave.success.message'));
        }
        catch (error) {
            logger.warn('Manual translation config save failed', error);
            setSaveError(t('adminSection.manualTranslation.saveError'));
        }
        finally {
            if (isMountedRef.current) {
                setSaving(false);
            }
        }
    };
    const startEditing = () => {
        if (!canModify || saving) {
            return;
        }
        setPendingValue(enabled);
        setSelectedLiveboard(null);
        clearUploadError();
        clearExistingCsvDownloadError();
        setSaveError(null);
        setDownloadError(null);
        setIsEditing(true);
        onEditingChange?.(true);
        setCsvExists(null);
        (async () => {
            const exists = await checkCsvExists();
            if (isMountedRef.current) {
                setCsvExists(exists);
            }
        })().catch(() => { });
    };
    const cancelEditing = () => {
        setPendingValue(enabled);
        setSelectedLiveboard(null);
        clearUploadError();
        clearExistingCsvDownloadError();
        setSaveError(null);
        setDownloadError(null);
        setIsEditing(false);
        onEditingChange?.(false);
    };
    const handleToggleChange = (value) => {
        setPendingValue(value);
    };
    const handleSaveClick = () => {
        handleSave(pendingValue);
    };
    const handleSelectorSearch = useCallback((pattern) => {
        setSelectorSearchPattern(pattern);
    }, []);
    const handleSelectLiveboard = useCallback((liveboard) => {
        setSelectedLiveboard(liveboard);
        setDownloadError(null);
        setSelectorSearchPattern('');
    }, []);
    const handleClearSelection = useCallback(() => {
        setSelectedLiveboard(null);
        setDownloadError(null);
    }, []);
    const handleDownload = useCallback(async () => {
        if (!selectedLiveboard) {
            return;
        }
        try {
            setDownloading(true);
            const result = await fetchPinboardDetail(selectedLiveboard.id);
            const { pinboard } = result;
            if (!pinboard) {
                throw new Error('Failed to load liveboard');
            }
            const liveboardData = transformToLiveboardData(pinboard, selectedLiveboard.name);
            await downloadLiveboardTranslations(liveboardData);
        }
        catch (error) {
            logger.warn('Manual translation download failed', error);
            setDownloadError(t('adminSection.manualTranslation.downloadError'));
        }
        finally {
            if (isMountedRef.current) {
                setDownloading(false);
            }
        }
    }, [selectedLiveboard, fetchPinboardDetail]);
    const availableLiveboards = React.useMemo(() => {
        const objects = pinboardListData?.getPinboardList?.objects || [];
        const pattern = selectorSearchPattern.toLowerCase();
        return objects
            .filter((lb) => !pattern || lb.name?.toLowerCase().includes(pattern))
            .map((lb) => ({
            id: lb.id,
            name: lb.name || lb.id,
        }));
    }, [pinboardListData, selectorSearchPattern]);
    if (loading) {
        return null;
    }
    return (React.createElement(ManualTranslationToggle, { enabled: enabled, pendingValue: pendingValue, onToggleChange: handleToggleChange, onSave: handleSaveClick, onEdit: startEditing, onCancel: cancelEditing, isEditing: isEditing, isClusterContext: isAllOrgContext, clusterEnabled: clusterEnabled, learnMoreUrl: LEARN_MORE_URL, saving: saving, canModify: canModify, onUploadCSV: handleUploadCSV, uploading: uploading, uploadError: uploadError, saveError: saveError, csvExists: csvExists, onDownloadExistingCsv: downloadExistingCsv, downloadingExistingCsv: downloadingExistingCsv, existingCsvDownloadError: existingCsvDownloadError, selectedLiveboard: selectedLiveboard, availableLiveboards: availableLiveboards, selectorLoading: pinboardListLoading, downloading: downloading, downloadError: downloadError, onSelectorSearch: handleSelectorSearch, onSelectLiveboard: handleSelectLiveboard, onClearSelection: handleClearSelection, onDownload: handleDownload }));
};
//# sourceMappingURL=manual-translation-toggle.container.js.map